import CommonContainerBox from "../custom/containers/CommonContainerBox";
import React, { forwardRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { convertDateIntoERPSDateFormat, formatAmount, getFormatDetails, statisticsFormatDate, formatIntDate, formatIntTime } from "../../utils/helper";
import { transactionStatusMap, FI }  from "../../constants/sharedConstants.js";
import dndEn from "../../assets/images/dndEn.png";
import { isBillerUser } from "../../utils/bpportal-common-utils";
import { ResponsiveBox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "../primitives";

const ERPSTransactionDetails = forwardRef((props, ref) => {
  const {
    data,
    recallReason,
    recalledBy,
    rejectReason,
    rejectedBy,
    recallRejectedByBillerReason,
    isShowBankCard = false,
    traceMessageList
  } = props;
  const { t } = useTranslation();

  const isRecall = (recallReason || recalledBy) ? true : false;
  const currentDate = new Date();
  const formattedDate = `${statisticsFormatDate(currentDate)},
                         ${currentDate.toLocaleTimeString(navigator.language, { hour: "2-digit", minute: "2-digit" })}`;

  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const isReturnsPortalLive = loginUser.commonConfig?.IS_RETURNS_PORTAL_LIVE === "Y";
  const [ctbId] = useState( isReturnsPortalLive && isBillerUser(loginUser.role?.role_type) && loginUser.billerCrossReferenceDto?.centralizedBillerId );

  return (
    <div ref={ref}>
      <ResponsiveBox className="manageUsers__page__title forPrint" sx={{ ml: 3, mb: 3 }}>
          <Typography variant="label">{formattedDate}</Typography>
          <br />
          <Typography variant="h2">
            {t("erps.app.title.message")}
          </Typography>
      </ResponsiveBox>
      <ResponsiveBox className="manageUsers__page__title forScreen" sx={{ ml: 3, mb:3 }}>
        {/* For Title update */}
        <CommonContainerBox title={t("erps.transactionEnquiry.page.transactionDetails.page.heading")}/>
        <Typography variant="h1">{t("erps.transactionEnquiry.page.transactionDetails.page.heading")}</Typography>
      </ResponsiveBox>

      <ResponsiveBox id={"screenshot-element"} sx={{ ml: 3 }}>
        <ResponsiveBox sx={{ mb: 1 }}>
          <Typography variant="h3">
            {t("erps.transactionEnquiry.page.transactionDetails.page.subheading")}
          </Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
            {t("erps.common.label.effectiveDate")}
            <Typography variant="body2">
              { convertDateIntoERPSDateFormat(data?.effective_date) }
            </Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.paymentDate/Time")}
            <Typography variant="body2">
              { formatIntDate(data?.payment_date) + " " + formatIntTime(data?.payment_time) }
            </Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.deliveryDate")}
            <Typography variant="body2">
              { convertDateIntoERPSDateFormat(data?.delivery_date) }
            </Typography>
          </Typography>
        </ResponsiveBox>

        <ResponsiveBox sx={{ mt: 2, display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
            {t("erps.common.label.paymentAmount")}
            <Typography variant="body2">{formatAmount(data?.transaction_amount)}</Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.confirmationNumber")}
            <Typography variant="body2">{data?.verification_number}</Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.auditNumber")}
            <Typography variant="body2">{data?.biller_file_number}</Typography>
          </Typography>
        </ResponsiveBox>

        <ResponsiveBox sx={{ mt: 2, display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
            {t("erps.common.label.transactionOrigination")}
            <Typography variant="body2">{`${ data?.cap_type || ""} ${ data?.caption_type_description || "" }`}</Typography>
          </Typography>

          { isReturnsPortalLive ? (   // Status - only shows if Returns Portal is live
              <Typography variant="label" className="transaction__enquiry__value left_space">
                {t("erps.common.label.status")}
                <Typography variant="body2">{ t(transactionStatusMap.get(data?.transaction_status), data?.transaction_status)}</Typography>
              </Typography>
            ) : (
              /* an empty field in this coolumn is needed for proper alignment when printing */
              <Typography className="transaction__enquiry__value left_space">{""}</Typography> 
            )
          }
        
          { (data.fi_number === FI.CIBC && data.correlation_id) ? (
            <Typography variant="label" className="transaction__enquiry__value left_space">
              {t("erps.transactionEnquiry.label.trackingNumber")}
              <Typography variant="body2">{data?.fi_tracking_number}</Typography>
            </Typography>
          ) : (
            /* Why this empty element? Because without it, the status (above) is misaligned in the Print view  */
            <Typography className="transaction__enquiry__value left_space"> </Typography>
          )}
        </ResponsiveBox>

        <ResponsiveBox sx={{ mt: 4, mb: 1 }}>
          <Typography variant="h3">
            {t("erps.common.label.billerDetails")}
          </Typography>
        </ResponsiveBox>

        <ResponsiveBox sx={{ display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
            {t("erps.common.label.corporateBiller")}
            <Typography variant="body2">
              {data?.biller_name} <br />
              {t("erps.common.label.attention")} {data?.primary_contact_name}{" "}
              <br />
              {t("erps.common.label.telephone")}{" "}
              {getFormatDetails(data?.primary_contact_phone)} <br />
              {t("erps.common.label.fax")}{" "}
              {getFormatDetails(data?.primary_contact_fax)} <br />
            </Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.billerID")}
            <Typography variant="body2">{data?.fi_biller_id || data?.display_biller_id}</Typography>
          </Typography>
          {ctbId ? (
                <Typography variant="label" className="transaction__enquiry__value left_space">
                  {t("erps.common.label.centralizedBillerId")}
                  <Typography variant="body2">{ctbId}</Typography>
                </Typography>
            ) : isReturnsPortalLive && data?.hasOwnProperty("pad_accepted_flag") && data?.pad_accepted_flag !== "X" ? (
                <Typography variant="label" className="transaction__enquiry__value left_space">
                  {t("erps.common.label.agreementAccepted")}
                  <Typography variant="body2">
                    {data.pad_accepted_flag === "Y" ? t("erps.common.label.yes") : t("erps.common.label.no")}
                  </Typography>
                </Typography>
            ) : (
                /* an empty field in the 3rd column is needed for proper alignment when printing */
                <Typography className="transaction__enquiry__value left_space">{""}</Typography>
            )}
        </ResponsiveBox>

        <ResponsiveBox sx={{ mt: 4, mb: 1 }}>
          <Typography variant="h3">{t("erps.common.label.customerDetails")}</Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
            {t("erps.common.label.customerAccountNumber")}
            <Typography variant="body2">{data?.biller_customer_number}</Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
            {t("erps.common.label.customerName")}
            <Typography variant="body2">{data?.fi_customer_name}</Typography>
          </Typography>
          
          {isShowBankCard ? (
            <Typography className="transaction__enquiry__value left_space">
              {t("erps.transactionEnquiry.componentToPrint.customerDetails.label.bankCardNumber")}
              <Typography variant="body2">
                {data.fi_customer_number_str}
              </Typography>
            </Typography>
          ) : (
            <Typography className="transaction__enquiry__value left_space">{""}</Typography>
          )}
        </ResponsiveBox>
      </ResponsiveBox>

      {(recallReason || recalledBy || rejectReason || rejectedBy) ? (
        // It is assumed that either the pair recallReason/recalledBy, or the pair rejectReason/rejectedBy will be passed, but not both
        <ResponsiveBox sx={{ ml: 3, mt: 3, display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value">
              {t(isRecall ? "erps.transactionEnquiry.label.recall.reason" : "erps.rejectpaymentmerchants.page.label.reject.reason")}
              <Typography variant="body2">{isRecall ? recallReason : rejectReason}</Typography>
          </Typography>
          <Typography variant="label" className="transaction__enquiry__value left_space">
              {t(isRecall ? "erps.transactionEnquiry.label.recalled.by" : "erps.rejectpaymentmerchants.page.label.rejected.by")}
              <Typography variant="body2">{isRecall ? recalledBy : rejectedBy}</Typography>
          </Typography>
          <Typography className="transaction__enquiry__value left_space">{""}</Typography> {/* 3rd field needed for proper alignment when printing */}
        </ResponsiveBox>
      ) : null
      }

      {recallRejectedByBillerReason?.length >= 0 ? (
        <ResponsiveBox sx={{ ml: 3, mt: 3, display: "flex" }}>
          <Typography variant="label" className="transaction__enquiry__value_2_columns">
              {t("erps.transactionEnquiry.label.billerRejectReason")}
              <Typography variant="body2">{recallRejectedByBillerReason}</Typography>
          </Typography>
          <Typography className="transaction__enquiry__value left_space">{""}</Typography> {/* 3rd field needed for proper alignment when printing */}
        </ResponsiveBox>
      ) : null
      }

      {(traceMessageList?.length > 0) ? (
          <ResponsiveBox sx={{ ml: 3, mt: 2, width: "80%" }}>
            <Typography variant="h3">
              {t("erps.tracequeue.label.messageActivity")}
            </Typography>
            <Table className="message_table_header" size="small" >
              <TableHead>
                <TableRow className="message_table_header_background">
                  <TableCell sx={{ width: "20%"}} className="message_table_cell_border">{t("erps.common.label.userId")}</TableCell>
                  <TableCell sx={{ width: "20%"}} className="message_table_cell_border">{t("erps.common.label.userName")}</TableCell>
                  <TableCell sx={{ width: "30%"}} className="message_table_cell_border">{t("erps.common.label.Date/Time")}</TableCell>
                  <TableCell sx={{ width: "60%"}} className="message_table_cell_border">{t("erps.common.label.comment")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {traceMessageList.map((msg, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="message_table_cell_border">{msg.userId}</TableCell>
                      <TableCell className="message_table_cell_border">{msg.userName}</TableCell>
                      <TableCell className="message_table_cell_border">{msg.date}</TableCell>
                      <TableCell className="message_table_cell_border">{msg.comment}</TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveBox>
      ) : null
      }

      <ResponsiveBox className="forPrint" sx={{ ml: 3, mr: "20px", mb: "10px", position: traceMessageList?.length > 0 ? "static" : "fixed", bottom: "0", width: "95%", paddingTop: "10px" }}>
        <hr style={{ height: "1px", border: "none", color: "rgba(224, 224, 224, 1)", backgroundColor: "rgba(224, 224, 224, 1)", paddingRight: "20px" }}/>
        <Typography sx={{ width: "180px" }} variant="p">
          <ResponsiveBox display={"flex"}>
            <ResponsiveBox>
              <p style={{ fontSize: "8px", marginBottom: "-12px", color: "#6D1652", paddingLeft: "10px" }}>
                powered by
              </p>
              <img src={dndEn} alt={"Dye and Durham"} height="60" />
            </ResponsiveBox>
            <ResponsiveBox sx={{ marginLeft: "auto" }} mt={3}>
              <Typography variant="label">{window.location.href}</Typography>
            </ResponsiveBox>
          </ResponsiveBox>
        </Typography>
      </ResponsiveBox>
    </div>
  );
});

export default ERPSTransactionDetails;
